import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import classNames from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { getActiveColorStyle } from "utils/colorUtils";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import { getTheme2SearchFilterColProps, getTheme2SearchFilterFieldCss } from "utils/theme2MultiSelect";
import {
  useTheme2FormPage,
  Theme2FormFieldsStyle,
  THEME2_ADMIN_INSETS,
} from "hooks/useTheme2FormPage";
import Select from "components/Select";
import { CKEditor } from "ckeditor4-react";
import { useGetCustomers, useSearchCustomers, useGetCustomerBulkSelectionMeta } from "api/customers/service";
import { useSendBulkMailToCustomers } from "api/mails/service";
import { useSendBulkSmsToCustomers } from "api/sms-service/service";
import {
  useGetCommunicationsTemplates,
  useGetCommunicationsTemplate,
} from "api/communications-templates/service";
import {
  CUSTOMER_ACCOUNT_FILTER_ALL,
  CUSTOMER_ACCOUNT_FILTER_ACTIVE_FINANCED,
} from "views/customers/components/CustomersFilters";

const getPlainTextFromHtml = (html) =>
  (html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const getBestPhone = (customer) => {
  const fields = ["cell_phone", "home_phone", "home_phone_2", "work_phone"];
  for (const field of fields) {
    const phone = customer?.[field];
    if (phone && String(phone).trim()) return String(phone).trim();
  }
  return null;
};

const PAGE_STYLES = (isTheme2, isDarkListTheme) => `
  ${isTheme2 ? `
  .sidebar.sidebar-shell-theme-2 ~ .main-panel:has(> .content.bulk-messaging-page) {
    height: auto !important;
    max-height: none !important;
    min-height: 100vh;
    overflow-y: auto !important;
    overflow-x: hidden;
  }
  @supports (height: 100dvh) {
    .sidebar.sidebar-shell-theme-2 ~ .main-panel:has(> .content.bulk-messaging-page) {
      min-height: 100dvh;
    }
  }
  .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.bulk-messaging-page.bulk-messaging-page--theme2 {
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
    display: block !important;
    flex: none !important;
  }
  .bulk-messaging-page.bulk-messaging-page--theme2 .bulk-messaging-table-card,
  .bulk-messaging-page.bulk-messaging-page--theme2 .bulk-messaging-table-card .card-body,
  .bulk-messaging-page.bulk-messaging-page--theme2 .inventory-table-wrap {
    overflow: visible !important;
    flex: none !important;
    min-height: auto !important;
  }
  ` : ""}
  ${isTheme2 ? getTheme2SearchFilterFieldCss({
    pageScopeSelector: ".bulk-messaging-page.bulk-messaging-page--theme2",
    filtersSelector: ".bulk-messaging-table-card .bulk-messaging-filters",
    rowClass: "bulk-messaging-filters-row",
    colClass: "bulk-messaging-filter-col",
  }) : ""}
  .bulk-messaging-card-header.card-header {
    padding-top: 2px;
    padding-bottom: 0;
  }
  .bulk-messaging-card-header h2 {
    font-size: 16px !important;
    margin-bottom: 0;
    line-height: 1.2;
  }
  .bulk-messaging-page .bulk-messaging-table-card .card-body {
    padding: 15px;
    padding-top: 10px;
    padding-bottom: 0 !important;
  }
  .bulk-messaging-page.bulk-messaging-page--theme2 .bulk-messaging-compose {
    padding-bottom: 8px;
  }
  .bulk-messaging-ckeditor {
    overflow: hidden;
    border: 1px solid #e3e3e3 !important;
    background: #fff;
  }
  .bulk-messaging-ckeditor .cke_chrome {
    border: none !important;
    box-shadow: none !important;
  }
  .bulk-messaging-ckeditor .cke_top {
    background: #ffffff !important;
    border-bottom: 1px solid #f0f0f0 !important;
  }
  .bulk-messaging-ckeditor .cke_bottom {
    background: #fafafa !important;
    border-top: 1px solid #f0f0f0 !important;
  }
  .bulk-messaging-page.bulk-messaging-page--theme2 .bulk-messaging-filters-row {
    padding-top: 4px;
    padding-bottom: 4px;
  }
  ${isDarkListTheme ? `
  body:not(.white-content) .bulk-messaging-page .bulk-messaging-filters label,
  body:not(.white-content) .bulk-messaging-page .bulk-messaging-compose label {
    color: #ffffff !important;
  }
  body:not(.white-content) .bulk-messaging-page .sticky-stock-table .rt-tbody .rt-td {
    color: #ffffff !important;
  }
  body:not(.white-content) .bulk-messaging-page .sticky-stock-table .rt-thead .rt-th .rt-resizable-header-content {
    color: #ffffff !important;
  }
  ` : ""}
  .sticky-stock-table .rt-table {
    width: 100%;
    min-width: 100%;
    border-collapse: collapse;
  }
  .sticky-stock-table .rt-thead .rt-th {
    text-align: center !important;
  }
  .sticky-stock-table .rt-thead .rt-th .rt-resizable-header-content {
    text-align: center;
    width: 100%;
    box-sizing: border-box;
  }
  .sticky-stock-table .rt-tbody .rt-td {
    text-align: center;
    vertical-align: middle;
  }
  .bulk-messaging-page .sticky-stock-table .rt-thead .rt-th:first-child {
    cursor: default !important;
  }
  .bulk-messaging-page .sticky-stock-table .rt-thead .rt-th:first-child.-sort-asc,
  .bulk-messaging-page .sticky-stock-table .rt-thead .rt-th:first-child.-sort-desc {
    box-shadow: none !important;
  }
  .bulk-messaging-page .sticky-stock-table .form-check {
    margin: 0;
    padding-left: 0;
    display: flex;
    justify-content: center;
  }
  .bulk-messaging-page .sticky-stock-table .form-check .form-check-label {
    padding-left: 0;
    width: 18px;
    height: 18px;
    margin-bottom: 0;
  }
  .bulk-messaging-page .sticky-stock-table .form-check input[type="checkbox"] {
    opacity: 0;
    position: absolute;
    visibility: hidden;
    width: 0;
    height: 0;
  }
  .bulk-messaging-page .sticky-stock-table .form-check .form-check-sign::before,
  .bulk-messaging-page .sticky-stock-table .form-check .form-check-sign::after {
    top: 0;
    left: 0;
  }
  ${getStickyStockTableStickyColumnCss("")}
`;

const BulkCheckbox = ({ checked, disabled, onChange, id }) => (
  <FormGroup check className="mb-0">
    <Label check htmlFor={id} className="mb-0">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="form-check-sign">
        <span className="check" />
      </span>
    </Label>
  </FormGroup>
);

const BulkMessagingSend = ({
  channel = "mail",
  activeColor = "blue",
  account,
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const { t } = useTranslation();
  const isMail = channel === "mail";
  const isTheme2 = shellThemeVariant === "2";
  const isDarkListTheme = !isLightTheme;
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const MessagingTable = isTheme2 ? ReactTable2 : ReactTable;

  const {
    inputStyle,
    inputClassName,
    selectStyles,
    theme2FieldsCss,
    contentClassName,
    formFieldsClassName,
  } = useTheme2FormPage({
    shellThemeVariant,
    activeColorStyle,
    pageKey: "bulk-messaging",
    insetClass: THEME2_ADMIN_INSETS.bulkMessaging,
    compact: true,
    withSelectStyles: true,
  });

  const editorRef = useRef(null);
  const lastAppliedTemplateRef = useRef(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [accountFilter, setAccountFilter] = useState(CUSTOMER_ACCOUNT_FILTER_ALL);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendResult, setSendResult] = useState(null);
  const searchTimer = useRef(null);

  const selectedTemplateId = selectedTemplate?.value;
  const { data: templatesData } = useGetCommunicationsTemplates(
    {
      status: "active",
      "pagination[pageSize]": 500,
      "pagination[page]": 1,
    },
    { enabled: isMail }
  );
  const { data: templateDetail, isFetching: templateLoading } = useGetCommunicationsTemplate(
    selectedTemplateId
  );

  const templateOptions = useMemo(() => {
    const rawList = templatesData?.data || templatesData?.results || [];
    return rawList.map((item) => ({
      label: item.name || item.template_id,
      value: item.template_id,
    }));
  }, [templatesData]);

  useEffect(() => {
    if (!selectedTemplateId || !isMail || templateLoading) return;
    if (lastAppliedTemplateRef.current === selectedTemplateId) return;

    const tpl = templateDetail?.data || templateDetail;
    if (!tpl?.template_id || String(tpl.template_id) !== String(selectedTemplateId)) return;

    lastAppliedTemplateRef.current = selectedTemplateId;
    setSubject(tpl.name || "");
    const layout = tpl.layout || "";
    setMessage(layout);
    if (editorRef.current) {
      editorRef.current.setData(layout);
    }
  }, [templateDetail, selectedTemplateId, isMail, templateLoading]);

  const handleTemplateChange = (option) => {
    setSelectedTemplate(option);
    if (!option) {
      lastAppliedTemplateRef.current = null;
    }
  };

  useEffect(() => {
    setSelectedTemplate(null);
    setSubject("");
    setMessage("");
    setSendResult(null);
    setSelectedIds([]);
    setSelectAllMatching(false);
    setAccountFilter(CUSTOMER_ACCOUNT_FILTER_ALL);
    lastAppliedTemplateRef.current = null;
    if (editorRef.current) {
      editorRef.current.setData("");
    }
  }, [channel]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  useEffect(() => {
    setSelectedIds([]);
    setSelectAllMatching(false);
  }, [accountFilter, debouncedSearch]);

  const isSearching = debouncedSearch.length >= 2;
  const { data: listData, isLoading: listLoading, isFetching: listFetching } = useGetCustomers(
    page,
    pageSize,
    !isSearching,
    { accountFilter }
  );
  const { data: searchData, isFetching: searchFetching } = useSearchCustomers(
    { name: debouncedSearch, ssn: debouncedSearch, accountFilter },
    isSearching
  );

  const customersData = isSearching ? searchData : listData;
  const isLoading = isSearching ? searchFetching : listLoading || listFetching;
  const customers = customersData?.results || [];
  const pagination = customersData?.pagination || {};

  const sendMailMutation = useSendBulkMailToCustomers();
  const sendSmsMutation = useSendBulkSmsToCustomers();
  const isSending = sendMailMutation.isPending || sendSmsMutation.isPending;

  const canSelect = useCallback(
    (customer) => (isMail ? Boolean(customer?.email?.trim()) : Boolean(getBestPhone(customer))),
    [isMail]
  );

  const toggleCustomer = useCallback(
    (customer) => {
      if (!canSelect(customer)) return;
      const id = String(customer.customer_id);
      if (selectAllMatching) {
        setSelectAllMatching(false);
        setSelectedIds([id]);
        return;
      }
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    },
    [canSelect, selectAllMatching]
  );

  const tableRows = useMemo(
    () =>
      customers.map((customer) => ({
        customer_id: customer.customer_id,
        full_name: customer.full_name || "—",
        contact: isMail ? customer.email || "—" : getBestPhone(customer) || "—",
        _raw: customer,
        _selectable: canSelect(customer),
      })),
    [customers, isMail, canSelect]
  );

  const selectableOnPage = tableRows.filter((row) => row._selectable);
  const pageIds = selectableOnPage.map((row) => String(row.customer_id));
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const totalMatching = pagination.total || 0;
  const showSelectAllBanner =
    allPageSelected &&
    !selectAllMatching &&
    totalMatching > pageIds.length;

  const { data: selectionMeta } = useGetCustomerBulkSelectionMeta(
    {
      accountFilter,
      search: isSearching ? debouncedSearch : undefined,
      contact: isMail ? "email" : "phone",
    },
    showSelectAllBanner || selectAllMatching
  );

  const selectedCount = selectAllMatching
    ? (selectionMeta?.selectable ?? totalMatching)
    : selectedIds.length;

  const toggleAllOnPage = useCallback(() => {
    if (selectAllMatching) {
      setSelectAllMatching(false);
      setSelectedIds([]);
      return;
    }
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  }, [allPageSelected, pageIds, selectAllMatching]);

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAllMatching(false);
  };

  const handleSelectAllMatching = () => {
    setSelectAllMatching(true);
  };

  const isRowSelected = useCallback(
    (customerId) =>
      selectAllMatching || selectedIds.includes(String(customerId)),
    [selectAllMatching, selectedIds]
  );

  const headerCheckboxChecked = selectAllMatching || allPageSelected;

  const columns = useMemo(
    () => [
      {
        id: "select",
        Header: () => (
          <BulkCheckbox
            id="bulk-select-all"
            checked={headerCheckboxChecked}
            disabled={pageIds.length === 0 && !selectAllMatching}
            onChange={toggleAllOnPage}
          />
        ),
        accessor: "select",
        disableSortBy: true,
        disableFilters: true,
        width: 52,
        Cell: ({ row }) => (
          <BulkCheckbox
            id={`bulk-select-${row.original.customer_id}`}
            checked={isRowSelected(row.original.customer_id)}
            disabled={!row.original._selectable}
            onChange={() => toggleCustomer(row.original._raw)}
          />
        ),
      },
      { Header: t("bulkMessaging.customerId"), accessor: "customer_id" },
      { Header: t("bulkMessaging.fullName"), accessor: "full_name" },
      {
        Header: isMail ? t("bulkMessaging.email") : t("bulkMessaging.phone"),
        accessor: "contact",
      },
    ],
    [
      headerCheckboxChecked,
      pageIds.length,
      toggleAllOnPage,
      isRowSelected,
      toggleCustomer,
      isMail,
      t,
      selectAllMatching,
    ]
  );

  const handleSend = async () => {
    const messageBody = isMail && editorRef.current ? editorRef.current.getData() : message.trim();
    const messagePlain = isMail ? getPlainTextFromHtml(messageBody) : messageBody;

    if (!messagePlain) return;
    if (!selectAllMatching && !selectedIds.length) return;
    if (selectAllMatching && !(selectionMeta?.selectable > 0)) return;
    if (isMail && !subject.trim()) return;

    setSendResult(null);

    try {
      const selectionPayload = selectAllMatching
        ? {
            account_filter: accountFilter,
            ...(isSearching && debouncedSearch ? { search: debouncedSearch } : {}),
          }
        : undefined;

      let result;
      if (isMail) {
        result = await sendMailMutation.mutateAsync({
          ...(selectAllMatching ? { selection: selectionPayload } : { customer_ids: selectedIds }),
          subject: subject.trim(),
          message: messageBody,
          ...(selectedTemplateId ? { template_id: selectedTemplateId } : {}),
        });
      } else {
        result = await sendSmsMutation.mutateAsync({
          ...(selectAllMatching ? { selection: selectionPayload } : { customer_ids: selectedIds }),
          message: messageBody,
        });
      }
      setSendResult(result?.data || null);
      setSelectedIds([]);
      setSelectAllMatching(false);
    } catch {
      // handled in mutation hooks
    }
  };

  const messagePlain = isMail ? getPlainTextFromHtml(message) : message.trim();

  const mailConfigured = account?.mail_profile?.host || account?.mail_id;
  const filterColProps = getTheme2SearchFilterColProps(isTheme2, {
    filterColClass: "bulk-messaging-filter-col",
    md: "4",
  });

  return (
    <div className={contentClassName}>
      <Theme2FormFieldsStyle css={theme2FieldsCss} />
      <style>{PAGE_STYLES(isTheme2, isDarkListTheme)}</style>

      <Card className="bulk-messaging-table-card">
        <CardHeader className="bulk-messaging-card-header">
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
            <h2 className="mb-0">
              {isMail ? t("bulkMessaging.mailTitle") : t("bulkMessaging.smsTitle")}
            </h2>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
              <span className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                {selectAllMatching
                  ? t("bulkMessaging.selectedAllMatching", { count: selectedCount })
                  : t("bulkMessaging.selectedCount", { count: selectedCount })}
              </span>
              <Button
                size="sm"
                outline
                color="secondary"
                disabled={selectedCount === 0}
                onClick={clearSelection}
              >
                {t("bulkMessaging.clearSelection")}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardBody>
          {isMail && !mailConfigured && (
            <Alert color="warning" className="mb-3">
              {t("bulkMessaging.mailProfileMissing")}
            </Alert>
          )}

          <div className={classNames("bulk-messaging-compose", formFieldsClassName)}>
            {isMail && (
              <FormGroup>
                <Label>{t("bulkMessaging.communicationTemplate")}</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t("bulkMessaging.selectTemplate")}
                  value={selectedTemplate}
                  onChange={handleTemplateChange}
                  isClearable
                  options={templateOptions}
                  isLoading={!templatesData && isMail}
                />
                {templateLoading && selectedTemplateId && (
                  <small className="text-muted d-block mt-1">{t("bulkMessaging.loadingTemplate")}</small>
                )}
              </FormGroup>
            )}

            {isMail && (
              <FormGroup>
                <Label>{t("bulkMessaging.subject")}</Label>
                <Input
                  type="text"
                  className={inputClassName()}
                  style={inputStyle}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t("bulkMessaging.subjectPlaceholder")}
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label>{t("bulkMessaging.message")}</Label>
              {isMail ? (
                <div className="bulk-messaging-ckeditor">
                  <CKEditor
                    key="bulk-mail-editor"
                    editorUrl="https://cdn.ckeditor.com/4.16.2/full/ckeditor.js"
                    initData={message}
                    config={{
                      toolbar: [
                        { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Strike", "-", "RemoveFormat"] },
                        { name: "paragraph", items: ["NumberedList", "BulletedList", "-", "Outdent", "Indent", "-", "Blockquote", "-", "JustifyLeft", "JustifyCenter", "JustifyRight", "JustifyBlock"] },
                        { name: "links", items: ["Link", "Unlink"] },
                        { name: "insert", items: ["Image", "Table", "HorizontalRule", "SpecialChar"] },
                        "/",
                        { name: "styles", items: ["Format", "Font", "FontSize"] },
                        { name: "colors", items: ["TextColor", "BGColor"] },
                        { name: "tools", items: ["Maximize", "Source"] },
                      ],
                      height: 400,
                      removePlugins: "exportpdf,elementspath",
                      resize_enabled: false,
                      skin: "moono-lisa",
                      versionCheck: false,
                      allowedContent: true,
                      extraAllowedContent: "span[*]{*}",
                    }}
                    onInstanceReady={({ editor }) => {
                      editorRef.current = editor;
                    }}
                    onChange={(evt) => {
                      setMessage(evt.editor.getData());
                    }}
                  />
                </div>
              ) : (
                <Input
                  type="textarea"
                  rows={4}
                  className={inputClassName()}
                  style={inputStyle}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("bulkMessaging.messagePlaceholder")}
                />
              )}
              <small className="text-muted d-block mt-1">{t("bulkMessaging.placeholderHint")}</small>
            </FormGroup>
          </div>

          <Row className={classNames("bulk-messaging-filters", { "bulk-messaging-filters-row": isTheme2 })}>
            <Col {...filterColProps}>
              <FormGroup className="mb-2">
                <Label>{t("customers.accountFilter")}</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  value={{
                    label:
                      accountFilter === CUSTOMER_ACCOUNT_FILTER_ACTIVE_FINANCED
                        ? t("customers.accountFilterActiveFinanced")
                        : t("customers.accountFilterAll"),
                    value: accountFilter,
                  }}
                  onChange={(option) => {
                    setAccountFilter(option?.value || CUSTOMER_ACCOUNT_FILTER_ALL);
                    setPage(1);
                  }}
                  options={[
                    { label: t("customers.accountFilterAll"), value: CUSTOMER_ACCOUNT_FILTER_ALL },
                    {
                      label: t("customers.accountFilterActiveFinanced"),
                      value: CUSTOMER_ACCOUNT_FILTER_ACTIVE_FINANCED,
                    },
                  ]}
                  isSearchable={false}
                />
              </FormGroup>
            </Col>
            <Col {...filterColProps}>
              <FormGroup className="mb-2">
                <Label>{t("bulkMessaging.searchCustomers")}</Label>
                <Input
                  type="text"
                  className={inputClassName()}
                  style={inputStyle}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t("bulkMessaging.searchPlaceholder")}
                />
              </FormGroup>
            </Col>
          </Row>

          {showSelectAllBanner && (
            <Alert color="info" className="py-2 px-3 mb-2">
              <span className="mr-2">
                {t("bulkMessaging.selectAllMatchingPrompt", {
                  pageCount: pageIds.length,
                  total: selectionMeta?.selectable ?? totalMatching,
                })}
              </span>
              <Button color="link" className="p-0 align-baseline" onClick={handleSelectAllMatching}>
                {t("bulkMessaging.selectAllMatchingAction")}
              </Button>
            </Alert>
          )}

          {selectAllMatching && (
            <Alert color="primary" className="py-2 px-3 mb-2">
              {t("bulkMessaging.selectedAllMatching", {
                count: selectionMeta?.selectable ?? totalMatching,
              })}
            </Alert>
          )}

          <div
            className="inventory-table-wrap"
            style={{ fontSize: isTheme2 ? "13px" : undefined, minWidth: 0, maxWidth: "100%" }}
          >
            <MessagingTable
              activeColor={activeColor}
              data={tableRows}
              columns={columns}
              className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
              manualPagination={!isSearching}
              pageCount={isSearching ? 1 : pagination.pageCount || 1}
              pageIndex={page - 1}
              totalItems={!isSearching ? pagination.total : undefined}
              pageSize={pageSize}
              noDataText={t("bulkMessaging.noRecipients")}
              loading={isLoading}
              loadingText={t("common.loading")}
              disableViewportClamp
              onPageChange={(pageIndex) => setPage(pageIndex + 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              onRowClick={(original) => {
                if (original?._raw) toggleCustomer(original._raw);
              }}
            />
          </div>

          {sendResult && (
            <Alert color={sendResult.failed > 0 ? "warning" : "success"} className="mt-3 mb-0">
              {t("bulkMessaging.sendSummary", {
                sent: sendResult.sent,
                failed: sendResult.failed,
                total: sendResult.total,
              })}
            </Alert>
          )}

          <div className="d-flex justify-content-end mt-3 mb-2">
            <Button
              size="sm"
              style={{ ...activeColorStyle, color: "white", fontWeight: 700 }}
              disabled={
                isSending ||
                selectedCount === 0 ||
                !messagePlain ||
                (isMail && !subject.trim()) ||
                (isMail && !mailConfigured) ||
                (isMail && templateLoading)
              }
              onClick={handleSend}
            >
              {isSending
                ? t("bulkMessaging.sending")
                : isMail
                  ? t("bulkMessaging.sendMail")
                  : t("bulkMessaging.sendSms")}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default BulkMessagingSend;
